// React
import { ChangeEvent, useRef, useState, useEffect } from 'react';
// Microsoft
import {
  Person,
  PersonCardInteraction,
  PersonViewType,
  PeoplePicker,
  PeoplePickerProps,
  LocalizationHelper,
} from '@microsoft/mgt-react';
// Utils
import { callFunction } from '../../lib/Helpers';
import { v4 as uuidv4 } from 'uuid';
// Components
import { BitButton } from '../BitButton';
import { AppObject } from '../AppCard';
import { Input } from '@fluentui/react-components';
import { Spinner } from '@fluentui/react';
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  TooltipHost,
  Icon,
  Theme,
} from '@fluentui/react';
// Styling
import {
  ColumnItemList,
  ManageButton,
  TableContainer,
  UserSettingsTableContainer,
  UserSettingsTableHeader,
} from './styles';
import { MagnifyingGlass, UserPlus, Gear } from '@phosphor-icons/react';
import { TeamsUserCredential } from '@microsoft/teamsfx';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

export interface IUserSettingsTableState {
  columns: IColumn[];
  items: IUserDocument[];
  selectionDetails: string;
  isModalSelection: boolean;
  isCompactMode: boolean;
  announcedMessage?: string;
  selectedPeople?: string[];
}

export interface ISubsDocument {
  name: string;
  membershipType: string;
}

export interface IUserApps {
  name: string;
  icon: string;
}

export interface IUserDocument {
  key: string;
  name: string;
  value: string;
  subs: ISubsDocument[];
  apps: IUserApps[];
}

type EntObject = {
  apps: string[];
  subscriptions: string[];
};

interface UserSettingsTableProps {
  appsObj: AppObject[];
  entObj: EntObject;
  theme?: Theme;
  themeName?: string;
  teamsUserCredential?: TeamsUserCredential;
  onManageItem: React.Dispatch<IUserDocument>;
  addedUsers: boolean;
  setHasAddedUser: (value: boolean) => void;
  refreshLoading: boolean;
}

export const UserSettingsTable = (props: UserSettingsTableProps) => {
  const pickerRef = useRef<PeoplePickerProps>();

  const { t } = useTranslation('settings');

  LocalizationHelper.strings = {
    _components: {
      'people-picker': {
        maxSelectionsPlaceHolder: 'Add one user at a time.',
      },
    },
  };

  const initialColumns: IColumn[] = [
    {
      key: 'column1',
      name: t('USERS_TABLE_COLUMN_USER'),
      fieldName: 'name',
      minWidth: 200,
      maxWidth: 300,
      isRowHeader: true,
      isResizable: true,
      isSorted: true,
      isSortedDescending: false,
      sortAscendingAriaLabel: 'Sorted A to Z',
      sortDescendingAriaLabel: 'Sorted Z to A',
      onColumnClick,
      data: 'string',
      onRender: (item: IUserDocument) => {
        return (
          <Person
            className={
              props.themeName !== 'default'
                ? 'mgt-dark mgt-person'
                : 'mgt-person'
            }
            userId={item.key}
            view={PersonViewType.twolines}
            showPresence
            personCardInteraction={PersonCardInteraction.none}
            line2Property="email"
          />
        );
      },
      isPadded: true,
    },
    {
      key: 'column2',
      name: t('USERS_TABLE_COLUMN_SUBSCRIPTIONS'),
      fieldName: 'subs',
      minWidth: 200,
      maxWidth: 250,
      isResizable: true,
      onColumnClick,
      data: 'string',
      onRender: (item: IUserDocument) => {
        return (
          <ColumnItemList>
            {item.subs.map((value) => {
              return (
                <div className="item" key={uuidv4()}>
                  <h1>{value.name}</h1>
                  <h2>({value.membershipType})</h2>
                </div>
              );
            })}
          </ColumnItemList>
        );
      },
    },
    {
      key: 'column3',
      name: t('USERS_TABLE_COLUMN_APPLICATIONS'),
      fieldName: 'apps',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onColumnClick: onColumnClick,
      data: 'string',
      onRender: (item: IUserDocument) => {
        return (
          <ColumnItemList>
            {item.apps.map((value) => {
              return (
                <div className="item" key={uuidv4()}>
                  <Icon iconName={value.icon} />
                  <h1>{value.name}</h1>
                </div>
              );
            })}
          </ColumnItemList>
        );
      },
    },
    {
      key: 'column4',
      name: t('USERS_TABLE_COLUMN_MANAGE'),
      ariaLabel: 'Column operations for Configure, Press to sort on Configure',
      fieldName: 'settings',
      minWidth: 40,
      maxWidth: 40,
      onRender: (item: IUserDocument) => (
        <ManageButton
          onClick={() => {
            props.onManageItem(item);
          }}
        >
          <TooltipHost content={t('USERS_MANAGE_TOOLTIP')}>
            <Gear
              size={24}
              weight="duotone"
              alt="Open settings panel for user"
            />
          </TooltipHost>
        </ManageButton>
      ),
    },
  ];

  const [allItems, setAllItems] = useState<IUserDocument[]>([]);
  const [columns, setColumns] = useState<IColumn[]>(initialColumns);
  const [items, setItems] = useState<IUserDocument[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [announcedMessage, setAnnouncedMessage] = useState('');
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);

  useEffect(() => {
    if (hasFetched === false) {
      const items = generateDocuments(props.entObj, props.appsObj);
      setAllItems(items);
      setItems(items);
    }
  }, [hasFetched]);

  function generateDocuments(entitlements: EntObject, apps: AppObject[]) {
    const items: IUserDocument[] = [];
    setHasFetched(true);
    entitlements.subscriptions.forEach((value, index) => {
      const subobj = JSON.parse(JSON.stringify(value));
      subobj.users.forEach((usrvalue: string) => {
        if (!items.find((usr) => usr.key === usrvalue))
          items.push({
            key: usrvalue,
            name: '',
            value: usrvalue,
            apps: [],
            subs: [],
          });

        callFunction(
          true,
          'getUserProfile?{0}&uid=' + usrvalue,
          undefined,
          false,
          props.teamsUserCredential
        )
          .then((data) => {
            items[
              items.findIndex((itemvalue) => itemvalue.key === usrvalue)
            ].name = data.displayName;
          })
          .catch((error) => {
            toast.error(t('USERS_ERROR_GETTING_DATA'));
          });

        subobj.planId.entitlements.forEach((ent: any) => {
          const app = apps.find((value) => value.id === ent.appid);
          if (app) {
            if (
              !items[
                items.findIndex((itemvalue) => itemvalue.key === usrvalue)
              ].apps.find((appv) => appv.name === app.name)
            )
              items[
                items.findIndex((itemvalue) => itemvalue.key === usrvalue)
              ].apps.push({ name: app.name, icon: app.iconName });
          }
        });

        const isOwner = usrvalue === subobj.beneficiary ? true : false;
        if (isOwner)
          items[
            items.findIndex((itemvalue) => itemvalue.key === usrvalue)
          ].subs.push({ name: subobj.name, membershipType: 'Owner' });
        else {
          const isAdmin = subobj.adminUsers.find(
            (usr: string) => usr === usrvalue
          )
            ? true
            : false;
          if (isAdmin)
            items[
              items.findIndex((itemvalue) => itemvalue.key === usrvalue)
            ].subs.push({ name: subobj.name, membershipType: 'Admin' });
          else
            items[
              items.findIndex((itemvalue) => itemvalue.key === usrvalue)
            ].subs.push({ name: subobj.name, membershipType: 'User' });
        }
      });
    });

    return items.slice(0).sort((a, b) => (a.name < b.name ? -1 : 1));
  }

  function onChangeFilter(ev: ChangeEvent) {
    const target = ev.target as HTMLInputElement;

    const newItems = allItems.filter((item) =>
      item.name.toLowerCase().includes(target.value.toLowerCase())
    );

    setItems(newItems);
  }

  function onClickAddUsers() {
    if (selectedPeople.length > 0) {
      const isImplicitContact = selectedPeople.find((user) => {
        const person = JSON.parse(JSON.stringify(user));
        return person.personType.subclass === 'ImplicitContact'
          ? person
          : undefined;
      });

      if (isImplicitContact) {
        toast.error(t('USERS_ERROR_NON_MS_ACCOUNT'));
        return;
      }

      const userExists = selectedPeople.find((user) => {
        const person = JSON.parse(JSON.stringify(user));
        return items.some((item) => item.key === person.id);
      });

      if (userExists) {
        toast.error(t('USERS_ERROR_USER_EXISTS'));
        return;
      }

      const newItems = [...allItems];

      selectedPeople.forEach((element) => {
        const people = JSON.parse(JSON.stringify(element));

        if (!newItems.find((i) => i.key === people.id)) {
          const newItem: IUserDocument = {
            key: people.id,
            name: people.displayName,
            value: people.id,
            apps: [],
            subs: [],
          };

          props.onManageItem(newItem);
        }
      });

      if (pickerRef.current) pickerRef.current.selectedPeople = [];
      setItems(newItems);
      props.setHasAddedUser(true);
    } else {
      toast.error(t('USERS_ERROR_USER_NO_SELECTION'));
    }
  }

  function onColumnClick(ev: React.MouseEvent<HTMLElement>, column: IColumn) {
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter(
      (currCol) => column.key === currCol.key
    )[0];

    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;

        setAnnouncedMessage(
          `${currColumn.name} is sorted ${
            currColumn.isSortedDescending ? 'descending' : 'ascending'
          }`
        );
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });

    const newItems = copyAndSort(
      items,
      currColumn.fieldName!,
      currColumn.isSortedDescending
    );

    setColumns(newColumns);
    setItems(newItems);
  }

  function copyAndSort<T>(
    items: T[],
    columnKey: string,
    isSortedDescending?: boolean
  ): T[] {
    const key = columnKey as keyof T;
    return items
      .slice(0)
      .sort((a: T, b: T) =>
        (isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1
      );
  }

  function onItemInvoked(item: IUserDocument) {}

  return (
    <UserSettingsTableContainer>
      <UserSettingsTableHeader>
        <Input
          className="filterInput"
          contentAfter={<MagnifyingGlass weight="duotone" />}
          placeholder={t('PANEL_FILTER_PLACEHOLDER')}
          onChange={onChangeFilter}
          id="step-filter-users"
        />

        <div className="addUserContainer" id="step-add-users">
          {props.refreshLoading && <Spinner size={2} />}
          <PeoplePicker
            disabled={props.addedUsers}
            ref={pickerRef}
            id="userPeoplePicker"
            className={
              (props.themeName !== 'default' ? 'mgt-dark' : '') +
              ' tableHeaderInput'
            }
            selectionMode="single"
            selectionChanged={(e) => {
              setSelectedPeople((e as CustomEvent).detail);
            }}
          />
          <BitButton
            label={t('PANEL_ADD_BUTTON')}
            icon={<UserPlus size={18} weight="duotone" />}
            action={onClickAddUsers}
          />
        </div>
      </UserSettingsTableHeader>
      <div className="tableView" id="step-manage-users">
        <TableContainer>
          <DetailsList
            className="table"
            items={items}
            columns={columns}
            selectionMode={SelectionMode.none}
            getKey={(item: IUserDocument) => item.key}
            setKey="none"
            layoutMode={DetailsListLayoutMode.justified}
            isHeaderVisible={true}
            onItemInvoked={onItemInvoked}
          />
          {items.length <= 0 && (
            <h2
              style={{
                width: '100%',
                textAlign: 'center',
                paddingTop: '2rem',
              }}
            >
              {t('USERS_EMPTY_LIST_MESSAGE')}
            </h2>
          )}
        </TableContainer>
      </div>
    </UserSettingsTableContainer>
  );
};
